import React from "react";

import { separateInputWrapperPropsAndInputProps } from "../../../utils";

import { SingleSelectInput, SingleSelectInputProps } from "../../../../../molecules/SelectInputs/SingleSelectInput";
import { usePyForm } from "../../../context";
import { BaseFormFieldProps, FormField } from "../../FormComponents/FormField";

// Get all unique props from SingleSelectInput that aren't in BaseFormFieldProps
type UniqueInputProps<T> = Omit<
	SingleSelectInputProps<T>,
	keyof BaseFormFieldProps<T> | "value" | "onChange" | "onBlur"
>;

export type FormSingleSelectProps<T> = Omit<BaseFormFieldProps<T>, "input"> & UniqueInputProps<T>;

/**
 * This is a form component that is used to create a single select dropdown.
 * It is a wrapper around the SingleSelectInput component.
 * It is used to create a single select dropdown that is connected to the PyForm context.
 * Its props cross-cut with the SingleSelectInput component and the FormField component.
 *
 * SingleSelectInput is a component for single selection inputs using Radix UI
 * Supports both 'select' (non-searchable) and 'combobox' (searchable) variants
 */
export const FormSingleSelect = <T,>(props: FormSingleSelectProps<T>) => {
	const form = usePyForm();
	const { inputWrapperProps, inputProps } = separateInputWrapperPropsAndInputProps(props, form.id);

	if (!form) {
		throw new Error("FormSingleSelect must be used within a Form component");
	}

	return (
		<FormField<T | null>
			{...inputWrapperProps}
			input={(field) => {
				return (
					<SingleSelectInput
						{...inputProps}
						options={props.options}
						selectionVariant={props.selectionVariant}
						placeholder={props.placeholder}
						onSearchChange={props.onSearchChange}
						isClearable={props.isClearable}
						value={field.value ?? undefined}
						onChange={field.onChange}
						id={inputWrapperProps.id}
						data-testid={`${inputWrapperProps["data-testid"]}-single-select`}
						disabled={inputWrapperProps.disabled}
					/>
				);
			}}
		/>
	);
};

export default FormSingleSelect;
